import { queryOptions, useMutation } from "@tanstack/react-query";
import partition from "lodash-es/partition";

import { sortString } from "@/common/utils/array";
import {
  CountryTo,
  update,
  UserUpdateRequest,
  validate,
} from "@/generated/api/users";

import { countriesQuery } from "../shared/api/countries.api";

const phoneCountryCodesQuery = ({ language }: { language: string }) =>
  queryOptions({
    ...countriesQuery(),
    select: (countries) => {
      const split = splitByLanguageMatch(countries, language);
      return sortCountriesBy((a) => a.phoneCountryCode, ...split);
    },
  });

const companyCountriesQuery = ({ language }: { language: string }) =>
  queryOptions({
    ...countriesQuery(),
    select: (countries) => {
      const available = countries.filter(
        (country) => country.hasCompanyValidator,
      );

      const split = splitByLanguageMatch(available, language);
      return sortCountriesBy((a) => a.localName, ...split);
    },
  });

const useCompanyNumberValidationMutation = () =>
  useMutation({
    mutationFn: ({
      companyNumber,
      countryCode,
    }: {
      countryCode: string;
      companyNumber: string;
    }) =>
      validate({
        companyNumber: companyNumber.replaceAll(" ", ""),
        countryCode,
      }),
    mutationKey: ["company-number-validation"],
  });

const useCompleteUserRegistrationMutation = () =>
  useMutation({
    mutationFn: (payload: UserUpdateRequest) =>
      update({
        company: payload.company,
        language: payload.language,
        name: payload.name,
        phone: payload.phone,
      }),
    mutationKey: ["user-registration"],
  });

const splitByLanguageMatch = (countries: CountryTo[], language: string) =>
  partition(countries, (country) => country.language == language);

const sortCountriesBy = (
  callback: (country: CountryTo) => string,
  ...countries: CountryTo[][]
) => {
  let result: CountryTo[] = [];

  countries.forEach((c) => {
    result = result.concat(...c.toSorted((a, b) => sortString(a, b, callback)));
  });

  return result;
};

export {
  companyCountriesQuery,
  phoneCountryCodesQuery,
  useCompanyNumberValidationMutation,
  useCompleteUserRegistrationMutation,
};
